import type { ProfitDistribution as Distribution } from "@/types";
import { formatCOP } from "@/utils/format";

const ROWS: { key: keyof Distribution; label: string; icon: string; bar: string }[] = [
  { key: "donation", label: "Fondo Donación", icon: "ri-heart-3-line", bar: "from-primary-400 to-primary-500" },
  { key: "advisor", label: "Fondo Asesor / Ayudante", icon: "ri-user-star-line", bar: "from-accent-400 to-accent-500" },
  { key: "reinvestment", label: "Fondo Caja Menor", icon: "ri-safe-2-line", bar: "from-secondary-400 to-secondary-500" },
  { key: "management", label: "Fondo Gerencia", icon: "ri-briefcase-4-line", bar: "from-accent-300 to-accent-400" },
];

interface ProfitDistributionProps {
  grossProfit: number;
  distribution: Distribution;
}

// Reparto de utilidades configurable (donación / asesor / reinversión / gerencia).
const ProfitDistribution = ({ grossProfit, distribution }: ProfitDistributionProps) => (
  <div className="glass-panel rounded-lg p-4 md:p-5">
    <div className="flex items-center gap-2 mb-1">
      <span className="w-8 h-8 rounded-md bg-accent-500/20 text-accent-200 flex items-center justify-center">
        <i className="ri-pie-chart-2-line" />
      </span>
      <h3 className="editable-title text-base font-bold">Reparto del 25%</h3>
    </div>
    <p className="text-[11px] text-foreground-500 mb-4">
      Distribución de la utilidad bruta estimada del período.
    </p>

    <div className="space-y-3">
      {ROWS.map((row) => {
        const pct = distribution[row.key];
        const amount = grossProfit * (pct / 100);
        return (
          <div key={row.key}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="flex items-center gap-1.5 text-xs text-foreground-700">
                <i className={`${row.icon} text-accent-300`} /> {row.label}
              </span>
              <span className="text-xs text-foreground-600">
                {pct}% · <span className="text-foreground-950 font-semibold">{formatCOP(amount)}</span>
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-background-300/40 overflow-hidden">
              <div
                className={`h-full rounded-full bg-gradient-to-r ${row.bar} transition-all duration-700`}
                style={{ width: `${Math.min(100, Math.max(0, pct * 4))}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>

    <div className="mt-4 rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2 flex items-center justify-between">
      <span className="text-[11px] text-foreground-500">Utilidad bruta del período</span>
      <span className="text-sm font-bold text-accent-200">{formatCOP(grossProfit)}</span>
    </div>
  </div>
);

export default ProfitDistribution;